import { getChatApiBase, getChatApiHeaders } from '../chatApiClient'
import axios from '../axiosInstance'

/**
 * Admin-global pinned conversations. Sent without `onBehalfOf`, so the caller is
 * `app_system`. The collection is declarative — the array IS the final state and
 * order, so there is no pin/unpin/reorder call. See docs/pin-conversations/.
 */

/** Not `…pinned.limit` — that one caps an end user's own pins, and both default to 5. */
export const PINNED_CONVERSATIONS_LIMIT_KEY =
  'features.ux.conversations.pinned.system.limit'

/** Fallback when the app has no configured value. */
export const PINNED_CONVERSATIONS_LIMIT_DEFAULT = 5

/**
 * The app's pin cap, configured server-side. Read up front because a breach only
 * reports the number inside `error.message`, with no structured field to parse.
 */
export function getPinnedConversationsLimit(appSettings?: {
  parameters?: Record<string, unknown>
}): number {
  const raw = appSettings?.parameters?.[PINNED_CONVERSATIONS_LIMIT_KEY]
  const value = Number(raw)
  return Number.isFinite(value) && value > 0 ? value : PINNED_CONVERSATIONS_LIMIT_DEFAULT
}

/** Order-significant entry: one key only — `uid` for 1-1, `guid` for a group. */
export type PinnedConversationTarget = { uid: string } | { guid: string }

export interface PinnedConversationUser {
  uid: string
  name?: string
  avatar?: string
  status?: string
  role?: string
  lastActiveAt?: number
  createdAt?: number
}

export interface PinnedConversationGroup {
  guid: string
  name?: string
  description?: string
  icon?: string
  type?: string
  membersCount?: number
  createdAt?: number
  owner?: string
}

export interface PinnedConversation {
  conversationType: 'user' | 'group'
  conversationWith: PinnedConversationUser | PinnedConversationGroup
  /** Groups only — a 1-1 id is viewer-dependent, so identity is type + uid/guid. */
  conversationId?: string
  /** Always `app_system` for this collection. */
  pinnedBy?: string
  /** Synthetic sort key, rewritten on every PUT — never render it as a date. */
  pinnedAt?: number
  /** Placeholders (`app_system`, `"0"`) — there is no unread state here. Never render. */
  uid?: string
  unreadMessageCount?: string
  lastReadMessageId?: string
  createdAt?: number
}

export interface PinnedConversationsResponse {
  data: PinnedConversation[]
  meta?: {
    pagination?: {
      total: number
      count: number
      per_page: number
      current_page: number
      total_pages: number
    }
  }
}

const pinnedUrl = (app: any, apiHost: string): string =>
  `${getChatApiBase(app, apiHost)}/conversations/pinned`

/**
 * GET /v3.0/conversations/pinned — the admin-global list, already ordered.
 * Returns `{ data: [] }` (never null) when nothing is pinned.
 */
export async function listPinnedConversations(
  app: any,
  apiHost: string,
): Promise<PinnedConversationsResponse> {
  return axios
    .get(pinnedUrl(app, apiHost), { headers: getChatApiHeaders(app) })
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}

/**
 * Replace the whole list. `targets` IS the final state and order; `[]` clears it.
 * All-or-nothing: one bad entry rejects the call and leaves the stored list intact.
 */
export async function replacePinnedConversations(
  app: any,
  apiHost: string,
  targets: PinnedConversationTarget[],
): Promise<PinnedConversationsResponse> {
  return axios
    .put(
      pinnedUrl(app, apiHost),
      { pinnedConversations: targets },
      { headers: getChatApiHeaders(app) },
    )
    .then(res => res.data)
    .catch(error => error?.response?.data ?? Promise.reject(error))
}
